<template>
  <v-row>
    <v-col v-if="'miqaatName' in miqaat">
      <v-alert v-if="isPreview" type="info" variant="tonal" density="compact" class="mb-2">
        Preview mode — submission disabled
      </v-alert>

      <v-card>
        <v-card-text>
          <h6 class="text-h6 font-weight-bold">
            {{ miqaat.miqaatName }} <br />
            <small>
              <span class="text-red">Event: {{ moment(miqaat.startDate).format("D-MMM-yyyy") }} to {{ moment(miqaat.endDate).format("D-MMM-yyyy") }}</span>
            </small><br />
            <small>
              <span class="text-red">Registration: {{ moment(miqaat.registrationStartDate).format("D-MMM-yyyy") }} to {{ moment(miqaat.registrationEndDate).format("D-MMM-yyyy") }}</span>
            </small>
          </h6>
          <h6 class="text-subtitle-1" v-if="miqaat.description" v-html="miqaat.description" />
          <v-row class="mt-4">
            <v-col cols="12" md="6" v-if="miqaat.registrationCommitmentAmount > 0">
              <div class="text-body-2 text-medium-emphasis">Registration Amount</div>
              <div class="text-h4 font-weight-bold">{{ miqaat.registrationCommitmentAmount }}</div>
            </v-col>
            <v-col cols="12" md="6" v-if="miqaat.accommodationAmount > 0">
              <div class="text-body-2 text-medium-emphasis">Accommodation Amount</div>
              <div class="text-h4 font-weight-bold">{{ miqaat.accommodationAmount }}</div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card class="my-2" v-if="!isPreview && miqaat.existingRegistrationStatus">
        <v-card-text>
          <v-alert type="info" variant="tonal">
            You have already registered. Status: <strong>{{ formatStatus(miqaat.existingRegistrationStatus) }}</strong>
          </v-alert>
          <v-btn
            v-if="miqaat.existingRegistrationStatus === 'Approved' && miqaat.existingRegistrationId"
            class="mt-2 mr-2"
            @click="router.push(`/miqaat-registrations/${miqaat.existingRegistrationId}/travel`)"
          >
            Fill Travel Details
          </v-btn>
        </v-card-text>
      </v-card>

      <MiqaatAdditionalForms
        v-if="!isPreview && miqaat.existingRegistrationStatus"
        :miqaat-id="route.params.id"
      />

      <template v-if="isPreview || !miqaat.existingRegistrationStatus">
        <v-card
          v-for="surveyForm in atRegistrationSurveys"
          :key="surveyForm.surveyId"
          class="my-2"
        >
          <v-card-text>
            <SurveyQuestionsForm
              :ref="(el) => setSurveyRef(surveyForm.surveyId, el)"
              :questions="surveyForm.questions || []"
              :title="surveyForm.surveyTitle"
              :description="surveyForm.description || ''"
              :required="surveyForm.isRequired"
              :allow-files="true"
            />
          </v-card-text>
        </v-card>

        <v-card class="my-2">
          <v-card-text v-if="miqaat.niazCommitmentRequired !== false">
            <v-text-field
              label="Niaz Commitment Amount"
              v-model.number="form.niazCommitment"
              type="number"
              suffix="USD"
              :disabled="isPreview"
            />
          </v-card-text>
          <v-card-text align="end" v-if="!isPreview">
            <v-btn @click="submit">Submit Registration</v-btn>
          </v-card-text>
        </v-card>
      </template>
    </v-col>
    <v-col v-else>
      <v-card>
        <v-card-text>
          <h6 class="text-h6 font-weight-bold text-red">Miqaat registration is not available</h6>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script setup>
import { reactive, ref, inject, onMounted, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import moment from "moment";
import {
  getActiveById,
  getMiqaatPreview,
  register,
  getRegistrationSurveyForms,
} from "@/services/miqaatService";
import { addDetail, addFiles } from "@/services/surveyService";
import SurveyQuestionsForm from "@/components/SurveyQuestionsForm.vue";
import MiqaatAdditionalForms from "./components/MiqaatAdditionalForms.vue";

const route = useRoute();
const router = useRouter();
const loader = inject("loader");
const snackbar = inject("snackBar");

const isPreview = computed(() => route.query.preview === "1");

const miqaat = ref({});
const atRegistrationSurveys = ref([]);
const surveyRefs = reactive({});
const form = reactive({
  niazCommitment: 0,
});

function setSurveyRef(surveyId, el) {
  if (el) surveyRefs[surveyId] = el;
  else delete surveyRefs[surveyId];
}

function formatStatus(status) {
  const map = {
    PendingIncharge: "Pending Incharge Approval",
    PendingCentral: "Pending Central Approval",
    Approved: "Approved",
    Rejected: "Rejected",
  };
  return map[status] || status;
}

async function loadData() {
  loader.show();
  try {
    const data = isPreview.value
      ? await getMiqaatPreview(route.params.id)
      : await getActiveById(route.params.id);
    miqaat.value = data || {};
    form.niazCommitment = 0;

    if (isPreview.value || !miqaat.value.existingRegistrationStatus) {
      atRegistrationSurveys.value =
        (await getRegistrationSurveyForms(route.params.id, "AtRegistration")) || [];
    } else {
      atRegistrationSurveys.value = [];
    }
  } catch (error) {
    snackbar.error(error);
    miqaat.value = {};
    atRegistrationSurveys.value = [];
  } finally {
    loader.hide();
  }
}

async function submit() {
  if (isPreview.value) return;

  const niazRequired = miqaat.value.niazCommitmentRequired !== false;
  const niazCompulsory = miqaat.value.niazCommitmentCompulsory !== false;
  const niazCommitment = niazRequired ? form.niazCommitment : 0;

  if (niazRequired && niazCompulsory && !niazCommitment) {
    snackbar.error("Niaz commitment amount is required");
    return;
  }

  const collectedBySurvey = [];
  for (const surveyForm of atRegistrationSurveys.value) {
    const ref = surveyRefs[surveyForm.surveyId];
    const collected = ref?.collectAnswers();
    if (!collected) return;
    collectedBySurvey.push({
      surveyId: surveyForm.surveyId,
      answers: collected.answers,
      files: collected.files,
    });
  }

  loader.show();
  try {
    for (const item of collectedBySurvey) {
      if (item.files?.length) {
        await addFiles(item.surveyId, item.files);
      }
      if (item.answers?.length) {
        await addDetail(item.surveyId, item.answers);
      }
    }

    await register(route.params.id, { niazCommitment });
    snackbar.success("Registration submitted successfully");
    await loadData();
  } catch (error) {
    snackbar.error(error);
  } finally {
    loader.hide();
  }
}

onMounted(loadData);
</script>
